import { AppConfig } from '../types/config';
import http from './http';
import { isStandalonePwa } from './is_mobile';
import { isBrokerage } from './brokerage_utils';

/**
 * Result posted back from the Office 365 calendar registration popup callback page
 * (served by the Web API at /api/calendar/registration/callback).
 */
interface Office365RegistrationMessage {
    source: string;
    success: boolean;
    message?: string;
}

const MESSAGE_SOURCE = 'agentconnect-o365-calendar';

/**
 * Requests the Microsoft authorize URL from the API to start the authorization-code + PKCE flow.
 *
 * The signed-in user's JWT is sent in the Authorization header of an authenticated POST (not in
 * the URL, since a full JWT exceeds web-server query-string length limits and would leak into logs
 * and browser history). credentials:'include' is required so the browser stores the cross-origin
 * PKCE/state cookie (SPA origin -> API origin), which is later returned on the Microsoft callback
 * navigation. The `mode` tells the API how to return control to the SPA (popup postMessage vs
 * same-tab redirect), and `origin` is a brand token the API resolves server-side for redirects.
 * The API persists the resulting refresh token server-side, so no token is returned to the browser.
 */
const requestAuthorizeUrl = async (mode: 'popup' | 'redirect'): Promise<string> => {
    const response: Response = await http('calendar/registration/authorize', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ mode, origin: isBrokerage ? 'excelsior' : 'agentconnect' }),
    });
    if (!response || !response.ok) {
        throw new Error('Unable to start Office 365 sign-in. Please try again.');
    }
    const body = await response.json();
    const authorizeUrl = body && body.authorizeUrl;
    if (!authorizeUrl) {
        throw new Error('Unable to start Office 365 sign-in. Please try again.');
    }
    return authorizeUrl;
};

/**
 * Connects the user's Office 365 calendar (authorization-code + PKCE flow).
 *
 * Installed PWAs (standalone display mode) cannot reliably use a popup: window.open kicks the user
 * out to the system browser and the opener relationship - and therefore postMessage - is lost. In
 * that case this navigates the CURRENT tab to Microsoft; the API callback then 302-redirects back
 * into the SPA at /settings/calendar?o365=success|error, which the settings page handles. The
 * returned promise never settles in redirect mode because the page unloads.
 *
 * In a normal browser tab/desktop it uses a popup and resolves once the callback posts its result.
 */
export const connectOffice365Calendar = (): Promise<Office365RegistrationMessage> => {
    if (isStandalonePwa()) {
        return connectViaRedirect();
    }
    return connectViaPopup();
};

/**
 * Same-tab redirect flow for installed PWAs. Navigates the current document to Microsoft once the
 * authorize URL is obtained; control returns to the SPA via the API's redirect-mode callback.
 */
const connectViaRedirect = (): Promise<Office365RegistrationMessage> => {
    return new Promise<Office365RegistrationMessage>((_resolve, reject) => {
        requestAuthorizeUrl('redirect')
            .then(authorizeUrl => {
                // Leaving the SPA; the promise intentionally never resolves.
                window.location.assign(authorizeUrl);
            })
            .catch((ex: Error) => {
                reject(ex instanceof Error ? ex : new Error('Unable to start Office 365 sign-in. Please try again.'));
            });
    });
};

/**
 * Popup flow for desktop and mobile-browser tabs. The popup is opened synchronously on the user
 * gesture (so it is not blocked) and then navigated to the Microsoft authorize URL.
 */
const connectViaPopup = (): Promise<Office365RegistrationMessage> => {
    return new Promise((resolve, reject) => {
        const apiOrigin = AppConfig.web_api;

        const width = 600;
        const height = 700;
        const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

        // Open the popup synchronously on the click so it is not blocked, then navigate it to the
        // authorize URL once the API returns it.
        const popup = window.open(
            'about:blank',
            'o365CalendarRegistration',
            `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
            reject(new Error('Popup was blocked. Please allow pop-ups and try again.'));
            return;
        }

        let settled = false;

        const cleanup = () => {
            window.removeEventListener('message', onMessage);
            window.clearInterval(closedTimer);
        };

        const fail = (error: Error) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            try {
                popup.close();
            } catch {
                // ignore
            }
            reject(error);
        };

        const onMessage = (event: MessageEvent) => {
            // Only trust messages from the API origin that serves the callback page.
            if (event.origin !== apiOrigin) {
                return;
            }
            const data = event.data as Office365RegistrationMessage;
            if (!data || data.source !== MESSAGE_SOURCE) {
                return;
            }
            settled = true;
            cleanup();
            try {
                popup.close();
            } catch {
                // ignore
            }
            if (data.success) {
                resolve(data);
            } else {
                reject(new Error(data.message || 'Office 365 sign-in failed.'));
            }
        };

        // Detect a user-closed popup so the caller is not left waiting forever.
        const closedTimer = window.setInterval(() => {
            if (popup.closed) {
                fail(new Error('Sign-in window was closed before completing.'));
            }
        }, 500);

        window.addEventListener('message', onMessage);

        requestAuthorizeUrl('popup')
            .then(authorizeUrl => {
                if (!settled) {
                    popup.location.href = authorizeUrl;
                }
            })
            .catch((ex: Error) => {
                fail(ex instanceof Error ? ex : new Error('Unable to start Office 365 sign-in. Please try again.'));
            });
    });
};

